import DynamicDialog from '@components/DynamicDialog';
import LoadingSpinner from '@components/LoadingSpinner';
import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
// import useClientSize from "@hooks/useClientSize";
// import { useAtom } from 'jotai';
// import { useTranslation } from 'react-i18next';

function ImageWithPreview({
    imgSrc,
    fallbackImgSrc,
    alt,
    useNaturalImageSize = false,
    small = false,
    maxPreview = { width: 290, height: 164 },
    fitParent = false,
    useMaxPreview = false,
    activatorProps = {},
}) {
    const [image, setImage] = useState();
    const [imageLoadError, setImageLoadError] = useState(false);
    // const [imageWrapper, setImageWrapper] = useState();
    // const imageWrapperClientSize = useClientSize(imageWrapper);
    //const { t } = useTranslation();

    // const maxPreviewWidth = 290;
    // const maxPreviewHeight = 164;

    useEffect(() => {
        if (!imgSrc) return;

        let isCancelled = false;
        let hasLoaded = false;
        const image = new Image();

        const timeoutId = setTimeout(() => {
            if (hasLoaded || isCancelled) return;

            // Try loading fallback image
            if (fallbackImgSrc && imgSrc !== fallbackImgSrc) {
                const fallbackImage = new Image();
                fallbackImage.onload = () => {
                    if (isCancelled) return;
                    setImageLoadError(false);
                    setImage(fallbackImage);
                };
                fallbackImage.onerror = () => {
                    if (isCancelled) return;
                    setImageLoadError(true);
                    setImage(null);
                };
                fallbackImage.src = fallbackImgSrc;
            } else {
                if (isCancelled) return;
                setImageLoadError(true);
                setImage(null);
            }
        }, 5000); // fallback after 5 seconds

        image.onload = () => {
            if (isCancelled || hasLoaded) return;
            hasLoaded = true;
            clearTimeout(timeoutId);
            setImageLoadError(false);
            setImage(image);
        };

        image.onerror = () => {
            if (isCancelled || hasLoaded) return;
            hasLoaded = true;
            clearTimeout(timeoutId);
            setImageLoadError(true);
            setImage(null);
        };

        image.src = imgSrc;

        // Cleanup on unmount or change
        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
        };
    }, [imgSrc, fallbackImgSrc]);


    const naturalSize = useMemo(() => {
        if (!image) return { width: 0, height: 0 };
        return {
            width: image.naturalWidth,
            height: image.naturalHeight,
        };
    }, [image]);

    const wrapperPaddingBottom = useMemo(() => {
        return (naturalSize.height / naturalSize.width) * 100 + '%';
    }, [naturalSize.width, naturalSize.height]);

    /*   const aspectRatio = useMemo(() => {
      return naturalSize.height / naturalSize.width;
    }, [naturalSize]); */

    const previewWidth = useMemo(() => {
        return Math.min(naturalSize.width, maxPreview.width);
    }, [maxPreview.width, naturalSize.width]);

    /*   const previewHeight = useMemo(() => {
    const height = previewWidth * aspectRatio;
    return Math.min(height, maxPreviewHeight);
  }, [previewWidth, aspectRatio]); */

    const wrapperStyle = {
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        width: `${useMaxPreview ? maxPreview.width : previewWidth}px`,
        height: `${maxPreview.height}px`,
    };

    const previewStyle = {
        position: 'absolute',
        objectFit: 'contain',
        top: 0,
        left: 0,
        width: '100%',
        height: 'auto',
    };

    const fitParentPreviewStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
    };

    if (imageLoadError) {
        return (
            <img
                alt='Placeholder image'
                src='https://assets.eye-able.com/dashboard-assets/icons/placeholder-image.png'
                style={{ objectFit: 'contain', padding: '6px', height: '70px' }}
            />
        );
    }

    if (!image) {
        return <LoadingSpinner minHeight='168px' />;
    }

    return (
        <DynamicDialog
            activatorProps={{
                'aria-label': alt,
                'sx': {
                    p: 0,
                    overflow: 'hidden',
                    height: '100%',
                    minHeight: '168px',
                    ...activatorProps,
                },
            }}
            activatorText={
                <Box aria-hidden sx={wrapperStyle}>
                    <img
                        alt={alt}
                        loading='eager'
                        src={image.src}
                        style={fitParent ? fitParentPreviewStyle : previewStyle}
                    />
                </Box>
            }
            fullWidth
            alwaysFullscreen={!small}
            aria-hidden
            hideActions
            title={alt}
        >
            {useNaturalImageSize ? (
                <Box
                    sx={{
                        textAlign: 'center',
                    }}
                >
                    <img
                        alt={alt}
                        loading='eager'
                        src={image.src}
                        style={{
                            zIndex: 1,
                        }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: 0,
                        outline: '4px solid black',
                        paddingBottom: wrapperPaddingBottom,
                    }}
                >
                    <img
                        alt={alt}
                        loading='eager'
                        src={image.src}
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            top: 0,
                            left: 0,
                            zIndex: 1,
                        }}
                    />
                </Box>
            )}
        </DynamicDialog>
    );
}

export default ImageWithPreview;
